import type { DocumentContext } from '@/document-render/domain/DocumentContext';
import type { TemplateLogoAlignment, TemplateLogoSize } from '@/templates/domain/types/TemplateLogo';
import { readDocumentBranding, readResolvedLogoUrlFromContext } from '@/document-render/application/documentBranding';

/**
 * Cover-logo defaults, shared by the HTML preview (`CoverPageRender`), the react-pdf export
 * (`CoverPageBlockPdf`) and the PDF logo pre-composite step. They mirror `DEFAULT_TEMPLATE_LOGO`
 * (top-of-page chrome, left, medium): a cover with no stored alignment starts where the Styles tab
 * says a new template's logo sits, not centred. `coverLogo.test.ts` keeps them equal.
 */
export const COVER_LOGO_DEFAULT_ALIGNMENT: TemplateLogoAlignment = 'left';
export const COVER_LOGO_DEFAULT_SIZE: TemplateLogoSize = 'medium';

export interface CoverLogoPresentation {
  /** Safe, resolved logo URL — null when the cover draws no logo. */
  url: string | null;
  size: TemplateLogoSize;
  alignment: TemplateLogoAlignment;
}

/**
 * What the cover page draws as its own logo. The logo is suppressed (`url: null`) when the block's
 * `showLogo` is off, when no safe URL resolves, and when the template places the logo in the footer
 * chrome (DB-4) — so the cover never double-renders it.
 */
export function resolveCoverLogo(showLogo: boolean, context: DocumentContext): CoverLogoPresentation {
  const branding = readDocumentBranding(context);
  const size = branding.logoSize ?? COVER_LOGO_DEFAULT_SIZE;
  const alignment = branding.logoAlignment ?? COVER_LOGO_DEFAULT_ALIGNMENT;
  if (!showLogo || branding.logoPosition === 'footer') return { url: null, size, alignment };
  return { url: readResolvedLogoUrlFromContext(context), size, alignment };
}
